import { Injectable, Optional } from '@angular/core';
import { Configuration } from '../../models/workflow/configuration';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { ConfigurationService } from '../../../entry-routes/configuration/configuration.service';
import { Url } from '../../models/url';
import { Observable } from 'rxjs';
import { CustomHttpUrlEncodingCodec } from '../../models/workflow/customHttpUrlEncodingCodecEncoder';
import {
  DataResponseHistoricTaskInstanceResponse
} from '../../models/workflow/dataResponseHistoricTaskInstanceResponse';

@Injectable({
  providedIn: 'root'
})
export class WkfHistoryTaskService {

  protected basePath;
  public configuration = new Configuration();

  constructor(
    private httpClient: HttpClient,
    private configAction: ConfigurationService,
    @Optional() configuration: Configuration
  ) {
    if ( configuration ) {
      this.configuration = configuration;
    }
  }

  private getBasePath(): string {
    if ( !this.basePath ) {
      const flowableProtectedApiBaseUrl = Url.getFlowableProtectedApiBaseUrl( this.configAction );
      console.debug( 'WkfHistoryTaskService, flowableProtectedApiBaseUrl : ' + flowableProtectedApiBaseUrl );
      this.basePath = flowableProtectedApiBaseUrl + Url.WKF_PROCESS_API;
    }
    console.debug( 'WkfHistoryTaskService, this.basePath : ' + this.basePath );
    return this.basePath;
  }

  /**
   * List historic task instances
   *
   * @param taskId An id of the historic task instance.
   * @param processInstanceId The process instance id of the historic task instance.
   * @param processInstanceIdWithChildren Selects the historic task instances for the process instance and its children.
   * @param processDefinitionKey The process definition key of the historic task instance.
   * @param processDefinitionKeyLike The process definition key of the historic task instance, which matches the given value.
   * @param processDefinitionId The process definition id of the historic task instance.
   * @param processDefinitionName The process definition name of the historic task instance.
   * @param processDefinitionNameLike The process definition name of the historic task instance, which matches the given value.
   * @param processBusinessKey The process instance business key of the historic task instance.
   * @param processBusinessKeyLike The process instance business key of the historic task instance that matches the given value.
   * @param executionId The execution id of the historic task instance.
   * @param taskDefinitionKey The task definition key for tasks part of a process
   * @param taskDefinitionKeys The task definition key for tasks part of a process
   * @param taskName The task name of the historic task instance.
   * @param taskNameLike The task name with like operator for the historic task instance.
   * @param taskDescription The task description of the historic task instance.
   * @param taskDescriptionLike The task description with like operator for the historic task instance.
   * @param taskCategory Select tasks with the given category. Note that this is the task category, not the category of the process definition (namespace within the BPMN Xml).
   * @param taskDeleteReason The task delete reason of the historic task instance.
   * @param taskDeleteReasonLike The task delete reason with like operator for the historic task instance.
   * @param taskAssignee The assignee of the historic task instance.
   * @param taskAssigneeLike The assignee with like operator for the historic task instance.
   * @param taskOwner The owner of the historic task instance.
   * @param taskOwnerLike The owner with like operator for the historic task instance.
   * @param taskInvolvedUser An involved user of the historic task instance
   * @param taskPriority The priority of the historic task instance.
   * @param finished Indication if the historic task instance is finished.
   * @param processFinished Indication if the process instance of the historic task instance is finished.
   * @param parentTaskId An optional parent task id of the historic task instance.
   * @param dueDate Return only historic task instances that have a due date equal this date.
   * @param dueDateAfter Return only historic task instances that have a due date after this date.
   * @param dueDateBefore Return only historic task instances that have a due date before this date.
   * @param withoutDueDate Return only historic task instances that have no due-date. When false is provided as value, this parameter is ignored.
   * @param taskCompletedOn Return only historic task instances that have been completed on this date.
   * @param taskCompletedAfter Return only historic task instances that have been completed after this date.
   * @param taskCompletedBefore Return only historic task instances that have been completed before this date.
   * @param taskCreatedOn Return only historic task instances that were created on this date.
   * @param taskCreatedBefore Return only historic task instances that were created before this date.
   * @param taskCreatedAfter Return only historic task instances that were created after this date.
   * @param includeTaskLocalVariables An indication if the historic task instance local variables should be returned as well.
   * @param includeProcessVariables An indication if the historic task instance global variables should be returned as well.
   * @param scopeDefinitionId Only return historic task instances with the given scopeDefinitionId.
   * @param scopeId Only return historic task instances with the given scopeId.
   * @param scopeType Only return historic task instances with the given scopeType.
   * @param tenantId Only return historic task instances with the given tenantId.
   * @param tenantIdLike Only return historic task instances with a tenantId like the given value.
   * @param withoutTenantId If true, only returns historic task instances without a tenantId set. If false, the withoutTenantId parameter is ignored.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public listHistoricTaskInstances(taskId?: string, processInstanceId?: string, processInstanceIdWithChildren?: string, processDefinitionKey?: string, processDefinitionKeyLike?: string, processDefinitionId?: string, processDefinitionName?: string, processDefinitionNameLike?: string, processBusinessKey?: string, processBusinessKeyLike?: string, executionId?: string, taskDefinitionKey?: string, taskDefinitionKeys?: string, taskName?: string, taskNameLike?: string, taskDescription?: string, taskDescriptionLike?: string, taskCategory?: string, taskDeleteReason?: string, taskDeleteReasonLike?: string, taskAssignee?: string, taskAssigneeLike?: string, taskOwner?: string, taskOwnerLike?: string, taskInvolvedUser?: string, taskPriority?: string, finished?: boolean, processFinished?: boolean, parentTaskId?: string, dueDate?: Date, dueDateAfter?: Date, dueDateBefore?: Date, withoutDueDate?: boolean, taskCompletedOn?: Date, taskCompletedAfter?: Date, taskCompletedBefore?: Date, taskCreatedOn?: Date, taskCreatedBefore?: Date, taskCreatedAfter?: Date, includeTaskLocalVariables?: boolean, includeProcessVariables?: boolean, scopeDefinitionId?: string, scopeId?: string, scopeType?: string, tenantId?: string, tenantIdLike?: string, withoutTenantId?: boolean, observe?: 'body', reportProgress?: boolean): Observable<DataResponseHistoricTaskInstanceResponse>;
  public listHistoricTaskInstances(taskId?: string, processInstanceId?: string, processInstanceIdWithChildren?: string, processDefinitionKey?: string, processDefinitionKeyLike?: string, processDefinitionId?: string, processDefinitionName?: string, processDefinitionNameLike?: string, processBusinessKey?: string, processBusinessKeyLike?: string, executionId?: string, taskDefinitionKey?: string, taskDefinitionKeys?: string, taskName?: string, taskNameLike?: string, taskDescription?: string, taskDescriptionLike?: string, taskCategory?: string, taskDeleteReason?: string, taskDeleteReasonLike?: string, taskAssignee?: string, taskAssigneeLike?: string, taskOwner?: string, taskOwnerLike?: string, taskInvolvedUser?: string, taskPriority?: string, finished?: boolean, processFinished?: boolean, parentTaskId?: string, dueDate?: Date, dueDateAfter?: Date, dueDateBefore?: Date, withoutDueDate?: boolean, taskCompletedOn?: Date, taskCompletedAfter?: Date, taskCompletedBefore?: Date, taskCreatedOn?: Date, taskCreatedBefore?: Date, taskCreatedAfter?: Date, includeTaskLocalVariables?: boolean, includeProcessVariables?: boolean, scopeDefinitionId?: string, scopeId?: string, scopeType?: string, tenantId?: string, tenantIdLike?: string, withoutTenantId?: boolean, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<DataResponseHistoricTaskInstanceResponse>>;
  public listHistoricTaskInstances(taskId?: string, processInstanceId?: string, processInstanceIdWithChildren?: string, processDefinitionKey?: string, processDefinitionKeyLike?: string, processDefinitionId?: string, processDefinitionName?: string, processDefinitionNameLike?: string, processBusinessKey?: string, processBusinessKeyLike?: string, executionId?: string, taskDefinitionKey?: string, taskDefinitionKeys?: string, taskName?: string, taskNameLike?: string, taskDescription?: string, taskDescriptionLike?: string, taskCategory?: string, taskDeleteReason?: string, taskDeleteReasonLike?: string, taskAssignee?: string, taskAssigneeLike?: string, taskOwner?: string, taskOwnerLike?: string, taskInvolvedUser?: string, taskPriority?: string, finished?: boolean, processFinished?: boolean, parentTaskId?: string, dueDate?: Date, dueDateAfter?: Date, dueDateBefore?: Date, withoutDueDate?: boolean, taskCompletedOn?: Date, taskCompletedAfter?: Date, taskCompletedBefore?: Date, taskCreatedOn?: Date, taskCreatedBefore?: Date, taskCreatedAfter?: Date, includeTaskLocalVariables?: boolean, includeProcessVariables?: boolean, scopeDefinitionId?: string, scopeId?: string, scopeType?: string, tenantId?: string, tenantIdLike?: string, withoutTenantId?: boolean, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<DataResponseHistoricTaskInstanceResponse>>;
  public listHistoricTaskInstances(taskId?: string, processInstanceId?: string, processInstanceIdWithChildren?: string, processDefinitionKey?: string, processDefinitionKeyLike?: string, processDefinitionId?: string, processDefinitionName?: string, processDefinitionNameLike?: string, processBusinessKey?: string, processBusinessKeyLike?: string, executionId?: string, taskDefinitionKey?: string, taskDefinitionKeys?: string, taskName?: string, taskNameLike?: string, taskDescription?: string, taskDescriptionLike?: string, taskCategory?: string, taskDeleteReason?: string, taskDeleteReasonLike?: string, taskAssignee?: string, taskAssigneeLike?: string, taskOwner?: string, taskOwnerLike?: string, taskInvolvedUser?: string, taskPriority?: string, finished?: boolean, processFinished?: boolean, parentTaskId?: string, dueDate?: Date, dueDateAfter?: Date, dueDateBefore?: Date, withoutDueDate?: boolean, taskCompletedOn?: Date, taskCompletedAfter?: Date, taskCompletedBefore?: Date, taskCreatedOn?: Date, taskCreatedBefore?: Date, taskCreatedAfter?: Date, includeTaskLocalVariables?: boolean, includeProcessVariables?: boolean, scopeDefinitionId?: string, scopeId?: string, scopeType?: string, tenantId?: string, tenantIdLike?: string, withoutTenantId?: boolean, observe: any = 'body', reportProgress = false ): Observable<any> {


    let queryParameters = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    if (taskId !== undefined && taskId !== null) {
      //queryParameters = queryParameters.set('taskId', <any>taskId);
      queryParameters = queryParameters.set('parentTaskId', <any>taskId);
    }
    if (processInstanceId !== undefined && processInstanceId !== null) {
      queryParameters = queryParameters.set('processInstanceId', <any>processInstanceId);
    }
    if (processInstanceIdWithChildren !== undefined && processInstanceIdWithChildren !== null) {
      queryParameters = queryParameters.set('processInstanceIdWithChildren', <any>processInstanceIdWithChildren);
    }
    if (processDefinitionKey !== undefined && processDefinitionKey !== null) {
      queryParameters = queryParameters.set('processDefinitionKey', <any>processDefinitionKey);
    }
    if (processDefinitionKeyLike !== undefined && processDefinitionKeyLike !== null) {
      queryParameters = queryParameters.set('processDefinitionKeyLike', <any>processDefinitionKeyLike);
    }
    if (processDefinitionId !== undefined && processDefinitionId !== null) {
      queryParameters = queryParameters.set('processDefinitionId', <any>processDefinitionId);
    }
    if (processDefinitionName !== undefined && processDefinitionName !== null) {
      queryParameters = queryParameters.set('processDefinitionName', <any>processDefinitionName);
    }
    if (processDefinitionNameLike !== undefined && processDefinitionNameLike !== null) {
      queryParameters = queryParameters.set('processDefinitionNameLike', <any>processDefinitionNameLike);
    }
    if (processBusinessKey !== undefined && processBusinessKey !== null) {
      queryParameters = queryParameters.set('processBusinessKey', <any>processBusinessKey);
    }
    if (processBusinessKeyLike !== undefined && processBusinessKeyLike !== null) {
      queryParameters = queryParameters.set('processBusinessKeyLike', <any>processBusinessKeyLike);
    }
    if (executionId !== undefined && executionId !== null) {
      queryParameters = queryParameters.set('executionId', <any>executionId);
    }
    if (taskDefinitionKey !== undefined && taskDefinitionKey !== null) {
      queryParameters = queryParameters.set('taskDefinitionKey', <any>taskDefinitionKey);
    }
    if (taskDefinitionKeys !== undefined && taskDefinitionKeys !== null) {
      queryParameters = queryParameters.set('taskDefinitionKeys', <any>taskDefinitionKeys);
    }
    if (taskName !== undefined && taskName !== null) {
      queryParameters = queryParameters.set('taskName', <any>taskName);
    }
    if (taskNameLike !== undefined && taskNameLike !== null) {
      queryParameters = queryParameters.set('taskNameLike', <any>taskNameLike);
    }
    if (taskDescription !== undefined && taskDescription !== null) {
      queryParameters = queryParameters.set('taskDescription', <any>taskDescription);
    }
    if (taskDescriptionLike !== undefined && taskDescriptionLike !== null) {
      queryParameters = queryParameters.set('taskDescriptionLike', <any>taskDescriptionLike);
    }
    if (taskCategory !== undefined && taskCategory !== null) {
      queryParameters = queryParameters.set('taskCategory', <any>taskCategory);
    }
    if (taskDeleteReason !== undefined && taskDeleteReason !== null) {
      queryParameters = queryParameters.set('taskDeleteReason', <any>taskDeleteReason);
    }
    if (taskDeleteReasonLike !== undefined && taskDeleteReasonLike !== null) {
      queryParameters = queryParameters.set('taskDeleteReasonLike', <any>taskDeleteReasonLike);
    }
    if (taskAssignee !== undefined && taskAssignee !== null) {
      queryParameters = queryParameters.set('taskAssignee', <any>taskAssignee);
    }
    if (taskAssigneeLike !== undefined && taskAssigneeLike !== null) {
      queryParameters = queryParameters.set('taskAssigneeLike', <any>taskAssigneeLike);
    }
    if (taskOwner !== undefined && taskOwner !== null) {
      queryParameters = queryParameters.set('taskOwner', <any>taskOwner);
    }
    if (taskOwnerLike !== undefined && taskOwnerLike !== null) {
      queryParameters = queryParameters.set('taskOwnerLike', <any>taskOwnerLike);
    }
    if (taskInvolvedUser !== undefined && taskInvolvedUser !== null) {
      queryParameters = queryParameters.set('taskInvolvedUser', <any>taskInvolvedUser);
    }
    if (taskPriority !== undefined && taskPriority !== null) {
      queryParameters = queryParameters.set('taskPriority', <any>taskPriority);
    }
    if (finished !== undefined && finished !== null) {
      queryParameters = queryParameters.set('finished', <any>finished);
    }
    if (processFinished !== undefined && processFinished !== null) {
      queryParameters = queryParameters.set('processFinished', <any>processFinished);
    }
    if (parentTaskId !== undefined && parentTaskId !== null) {
      queryParameters = queryParameters.set('parentTaskId', <any>parentTaskId);
    }
    if (dueDate !== undefined && dueDate !== null) {
      queryParameters = queryParameters.set('dueDate', <any>dueDate.toISOString());
    }
    if (dueDateAfter !== undefined && dueDateAfter !== null) {
      queryParameters = queryParameters.set('dueDateAfter', <any>dueDateAfter.toISOString());
    }
    if (dueDateBefore !== undefined && dueDateBefore !== null) {
      queryParameters = queryParameters.set('dueDateBefore', <any>dueDateBefore.toISOString());
    }
    if (withoutDueDate !== undefined && withoutDueDate !== null) {
      queryParameters = queryParameters.set('withoutDueDate', <any>withoutDueDate);
    }
    if (taskCompletedOn !== undefined && taskCompletedOn !== null) {
      queryParameters = queryParameters.set('taskCompletedOn', <any>taskCompletedOn.toISOString());
    }
    if (taskCompletedAfter !== undefined && taskCompletedAfter !== null) {
      queryParameters = queryParameters.set('taskCompletedAfter', <any>taskCompletedAfter.toISOString());
    }
    if (taskCompletedBefore !== undefined && taskCompletedBefore !== null) {
      queryParameters = queryParameters.set('taskCompletedBefore', <any>taskCompletedBefore.toISOString());
    }
    if (taskCreatedOn !== undefined && taskCreatedOn !== null) {
      queryParameters = queryParameters.set('taskCreatedOn', <any>taskCreatedOn.toISOString());
    }
    if (taskCreatedBefore !== undefined && taskCreatedBefore !== null) {
      queryParameters = queryParameters.set('taskCreatedBefore', <any>taskCreatedBefore.toISOString());
    }
    if (taskCreatedAfter !== undefined && taskCreatedAfter !== null) {
      queryParameters = queryParameters.set('taskCreatedAfter', <any>taskCreatedAfter.toISOString());
    }
    if (includeTaskLocalVariables !== undefined && includeTaskLocalVariables !== null) {
      queryParameters = queryParameters.set('includeTaskLocalVariables', <any>includeTaskLocalVariables);
    }
    if (includeProcessVariables !== undefined && includeProcessVariables !== null) {
      queryParameters = queryParameters.set('includeProcessVariables', <any>includeProcessVariables);
    }
    if (scopeDefinitionId !== undefined && scopeDefinitionId !== null) {
      queryParameters = queryParameters.set('scopeDefinitionId', <any>scopeDefinitionId);
    }
    if (scopeId !== undefined && scopeId !== null) {
      queryParameters = queryParameters.set('scopeId', <any>scopeId);
    }
    if (scopeType !== undefined && scopeType !== null) {
      queryParameters = queryParameters.set('scopeType', <any>scopeType);
    }
    if (tenantId !== undefined && tenantId !== null) {
      queryParameters = queryParameters.set('tenantId', <any>tenantId);
    }
    if (tenantIdLike !== undefined && tenantIdLike !== null) {
      queryParameters = queryParameters.set('tenantIdLike', <any>tenantIdLike);
    }
    if (withoutTenantId !== undefined && withoutTenantId !== null) {
      queryParameters = queryParameters.set('withoutTenantId', <any>withoutTenantId);
    }

    let headers = new HttpHeaders();

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'application/json'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.get<DataResponseHistoricTaskInstanceResponse>(Url.getFlowableProtectedApiBaseUrl(this.configAction)+Url.WKF_TASK_HISTORY+`historic-task-instances`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress
      }
    );
  }
}
